/* eslint-disable */
import React, { useEffect, useState, useRef, useCallback } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faHome,
  faCalendarAlt,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import moment from "moment";
import { Chart, registerables } from "chart.js";
import CanvasJSReact from "@canvasjs/react-charts";
import { subMonths, startOfMonth } from "date-fns";

// Register Chart.js components
Chart.register(...registerables);
const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const SubscriberAnalysisChart = () => {
  // New: granular loading states for different sections
  const [loadingStates, setLoadingStates] = useState({
    summary: false,
    funnels: false,
    lead_source: false,
    lead_conversion_rate: false,
    lead_status: false,
    subscriber_status: false,
    exp_in_market: false,
    investments: false,
    convertible: false,
    utm_source: false
  });

  const [chartInstances, setChartInstances] = useState({});
  const chartRefs = useRef({});

  // Default start date (last month start)
  const prevMonthStart = startOfMonth(subMonths(new Date(), 1));

  // Calculate Current Week for Default Display
  const defaultStart = moment().startOf("isoWeek");
  const defaultEnd = moment().endOf("isoWeek");
  const defaultLabel = `${defaultStart.format(
    "DD-MM-YYYY"
  )} - ${defaultEnd.format("DD-MM-YYYY")}`;

  // Form state - Initialized with Current Week
  const [formInputs, setFormInputs] = useState({
    created_on: defaultLabel,
    screated_on: defaultStart.toDate(),
    ecreated_on: defaultEnd.toDate(),
  });

  // Filter state - Initialized with Current Week
  const [filters, setFilters] = useState({
    created_on: defaultLabel,
    screated_on: defaultStart.format("DD-MM-YYYY"),
    ecreated_on: defaultEnd.format("DD-MM-YYYY"),
  });

  // Report data state
  const [reportData, setReportData] = useState({
    // Summary data
    subscriberCount: 0,
    overallConversionRate: "0",
    totalSubscribed: 0,
    subscriberActiveCount: 0,

    // Chart data
    chartData: {},
    allDates: [],

    // Funnel data
    leadsLineChartData: {},
    leadQualityLineChartData: {},

    // Count data for additional metrics
    countData: {}
  });

  // Define Ranges for the Bootstrap Daterangepicker
  const ranges = {
    "This Week": [moment().startOf("isoWeek"), moment().endOf("isoWeek")],
    Today: [moment(), moment()],
    Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
    "Last 7 Days": [moment().subtract(6, "days"), moment()],
    "Last 30 Days": [moment().subtract(29, "days"), moment()],
    "This Month": [moment().startOf("month"), moment().endOf("month")],
    "Last Month": [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
    "This Year": [moment().startOf("year"), moment().endOf("year")],
    "Last Year": [
      moment().subtract(1, "year").startOf("year"),
      moment().subtract(1, "year").endOf("year"),
    ],
  };

  // API endpoints configuration
  const API_ENDPOINTS = {
    charts: {
      lead_source: "lead-source",
      lead_conversion_rate: "lead-conversion-rate",
      lead_status: "lead-status",
      subscriber_status: "subscriber-status",
      exp_in_market: "experience-in-market",
      investments: "investment-capacity",
      convertible: "convertible",
      utm_source: "utm-source"
    },
    counts: {
      salesTeamCalls: "sales-team-calls",
      totalSubscribed: "total-subscribed"
    },
    composite: {
      summary: "summary",
      leadsLineChart: "leads-line-chart",
      leadQualityLineChart: "lead-quality-line-chart"
    },
    rates: {
      overallConversion: "overall-conversion"
    }
  };

  const chartSections = [
    "lead_source",
    "lead_conversion_rate",
    "lead_status",
    "subscriber_status",
    "exp_in_market",
    "investments",
    "convertible",
  ];

  // Helper function to make API calls
  const makeApiCall = async (endpoint, type = "chart") => {
    try {
      const token = localStorage.getItem("authToken");
      const baseUrl = `${API_URL}report/subscriber-analysis`;

      let url;
      switch (type) {
        case "chart":
          url = `${baseUrl}/chart/${endpoint}`;
          break;
        case "count":
          url = `${baseUrl}/count/${endpoint}`;
          break;
        case "composite":
          url = `${baseUrl}/composite/${endpoint}`;
          break;
        case "rate":
          url = `${baseUrl}/rate/${endpoint}`;
          break;
        default:
          url = `${baseUrl}/chart/${endpoint}`;
      }

      const response = await axios.get(url, {
        params: { created_on: filters.created_on },
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "*/*",
        },
      });

      return response.data;
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      return null;
    }
  };

  // Function to update loading state for a specific key
  const updateLoadingState = (key, value) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  // Fetch all data independently to allow individual loading
  const fetchAllData = useCallback(() => {
    // 1. Set all to loading initially
    const allKeys = Object.keys(loadingStates);
    const initialLoadState = allKeys.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setLoadingStates(initialLoadState);

    // 2. Fetch Summary Data (Composite)
    const fetchSummary = async () => {
      const summaryData = await makeApiCall(API_ENDPOINTS.composite.summary, "composite");
      const rateData = await makeApiCall(API_ENDPOINTS.rates.overallConversion, "rate");

      setReportData(prev => ({
        ...prev,
        subscriberCount: summaryData?.subscriberCount || 0,
        subscriberActiveCount: summaryData?.subscriberActiveCount || 0,
        totalSubscribed: summaryData?.totalSubscribed || 0,
        overallConversionRate: rateData?.rate || "0",
      }));
      updateLoadingState('summary', false);
    };

    // 3. Fetch Funnel Data (Composite)
    const fetchFunnels = async () => {
      const [leadsLineData, leadQualityData] = await Promise.all([
        makeApiCall(API_ENDPOINTS.composite.leadsLineChart, "composite"),
        makeApiCall(API_ENDPOINTS.composite.leadQualityLineChart, "composite")
      ]);

      setReportData(prev => ({
        ...prev,
        leadsLineChartData: leadsLineData || {},
        leadQualityLineChartData: leadQualityData || {},
      }));
      updateLoadingState('funnels', false);
    };

    // 4. Fetch Individual Charts
    const fetchIndividualChart = async (chartKey) => {
      const data = await makeApiCall(API_ENDPOINTS.charts[chartKey], "chart");

      if (data) {
        setReportData(prev => ({
          ...prev,
          chartData: {
            ...prev.chartData,
            [chartKey]: data.chartData || {}
          },
          allDates: (data.allDates && data.allDates.length > 0) ? data.allDates : prev.allDates
        }));
      }
      updateLoadingState(chartKey, false);
    };

    // Execute fetches
    fetchSummary();
    fetchFunnels();

    // Loop through defined chart sections and fetch them individually
    chartSections.forEach(key => {
      fetchIndividualChart(key);
    });

  }, [filters]);

  // Main data fetching effect
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Initialize charts when data changes
  useEffect(() => {
    if (Object.keys(reportData.chartData).length > 0 || Object.keys(reportData.leadsLineChartData).length > 0) {
      initializeCharts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      Object.values(chartInstances).forEach((chart) => {
        if (chart) chart.destroy();
      });
    };
  }, []);

  const initializeCharts = () => {
    const newChartInstances = { ...chartInstances };

    Object.keys(reportData.chartData).forEach((ckey) => {
      const lineChartId = `lineChart_${ckey}`;
      const pieChartId = `pieChart_${ckey}`;

      if (reportData.chartData[ckey]) {
        if (chartRefs.current[lineChartId]) {
          const ctx = chartRefs.current[lineChartId].getContext("2d");
          const chartData = reportData.chartData[ckey];

          if (newChartInstances[lineChartId]) {
            newChartInstances[lineChartId].destroy();
          }

          newChartInstances[lineChartId] = new Chart(ctx, {
            type: "bar",
            data: {
              labels: reportData.allDates,
              datasets: Object.keys(chartData).map((key) => ({
                label: key === "" ? "Unknown" : key,
                backgroundColor: chartData[key].color || "#ccc",
                data: typeof chartData[key].data === "string"
                  ? chartData[key].data.split(",").map((num) => Number(num.trim()))
                  : (chartData[key].piedata || []),
              })),
            },
            options: {
              plugins: {
                title: {
                  display: true,
                  text: getChartTitle(ckey),
                },
                tooltip: {
                  mode: "index",
                  intersect: false,
                },
                legend: { position: "bottom" },
              },
              scales: {
                x: {
                  stacked: true,
                  grid: { display: false },
                },
                y: {
                  stacked: true,
                  beginAtZero: true,
                },
              },
              responsive: true,
              maintainAspectRatio: false,
            },
          });
        }

        if (chartRefs.current[pieChartId]) {
          const ctx = chartRefs.current[pieChartId].getContext("2d");
          const chartData = reportData.chartData[ckey];

          if (newChartInstances[pieChartId]) {
            newChartInstances[pieChartId].destroy();
          }

          newChartInstances[pieChartId] = new Chart(ctx, {
            type: "pie",
            data: {
              labels: Object.keys(chartData).map((k) =>
                k === "" ? "Unknown" : k
              ),
              datasets: [
                {
                  data: Object.values(chartData).map(
                    (val) => Number(val.totalcount) || 0
                  ),
                  backgroundColor: Object.values(chartData).map(
                    (val) => val.color || "#ccc"
                  ),
                },
              ],
            },
            options: {
              plugins: {
                title: {
                  display: true,
                  text: getChartTitle(ckey),
                },
                legend: { position: "bottom" },
              },
              responsive: true,
              maintainAspectRatio: false,
            },
          });
        }
      }
    });

    if (chartRefs.current.statisticsChart && Object.keys(reportData.leadsLineChartData).length > 0) {
      const ctx = chartRefs.current.statisticsChart.getContext("2d");
      if (newChartInstances.statisticsChart)
        newChartInstances.statisticsChart.destroy();

      newChartInstances.statisticsChart = new Chart(ctx, {
        type: "line",
        data: {
          labels: Object.keys(reportData.leadsLineChartData),
          datasets: [
            {
              label: "Count",
              data: Object.values(reportData.leadsLineChartData).map((v) =>
                Number(v)
              ),
              borderColor: "rgb(66,165,245)",
              backgroundColor: "rgba(66,165,245,0.5)",
              pointStyle: "circle",
              pointRadius: 6,
              fill: false,
              tension: 0,
            },
          ],
        },
        options: {
          plugins: {
            title: { display: true, text: "Statistics Chart" },
            legend: { display: false, position: "bottom" },
          },
          scales: {
            y: { beginAtZero: true },
          },
          responsive: true,
          maintainAspectRatio: false,
        },
      });
    }

    if (chartRefs.current.leadQualityStatisticsChart && Object.keys(reportData.leadQualityLineChartData).length > 0) {
      const ctx = chartRefs.current.leadQualityStatisticsChart.getContext("2d");
      if (newChartInstances.leadQualityStatisticsChart)
        newChartInstances.leadQualityStatisticsChart.destroy();

      newChartInstances.leadQualityStatisticsChart = new Chart(ctx, {
        type: "line",
        data: {
          labels: Object.keys(reportData.leadQualityLineChartData),
          datasets: [
            {
              label: "Count",
              data: Object.values(reportData.leadQualityLineChartData).map(
                (v) => Number(v)
              ),
              borderColor: "rgb(66,165,245)",
              backgroundColor: "rgba(66,165,245,0.5)",
              pointStyle: "circle",
              pointRadius: 6,
              fill: false,
              tension: 0,
            },
          ],
        },
        options: {
          plugins: {
            title: { display: true, text: "Lead Statistics Chart" },
            legend: { display: false, position: "bottom" },
          },
          scales: {
            y: { beginAtZero: true },
          },
          responsive: true,
          maintainAspectRatio: false,
        },
      });
    }

    setChartInstances(newChartInstances);
  };

  const getChartTitle = (ckey) => {
    const titles = {
      lead_source: "Lead Source",
      lead_conversion_rate: "Lead Conversion Rate in Number",
      lead_status: "Lead Status",
      subscriber_status: "Subscriber Status",
      exp_in_market: "Exp. in Market",
      investments: "Investments",
      convertible: "Convertible",
      utm_source: "UTM Source",
    };
    return titles[ckey] || ckey;
  };

  const getSectionTitle = (ckey) => {
    const titles = {
      lead_source: "Lead Source",
      lead_conversion_rate: "Lead Conversion Rate",
      lead_status: "Lead Status",
      subscriber_status: "Subscriber Status",
      exp_in_market: "Exp. in Market",
      investments: "Investments",
      convertible: "Convertible",
    };
    return titles[ckey] || ckey;
  };

  // Daterangepicker Callback
  const handleDateCallback = (event, picker) => {
    const start = picker.startDate;
    const end = picker.endDate;

    const dateValue = `${start.format("DD-MM-YYYY")} - ${end.format(
      "DD-MM-YYYY"
    )}`;

    setFormInputs((prev) => ({
      ...prev,
      created_on: dateValue,
      screated_on: start.toDate(),
      ecreated_on: end.toDate(),
    }));
  };

  // Apply filters
  const applyFilters = (e) => {
    e.preventDefault();
    setFilters({
      created_on: formInputs.created_on,
      screated_on: formInputs.screated_on
        ? moment(formInputs.screated_on).format("DD-MM-YYYY")
        : "",
      ecreated_on: formInputs.ecreated_on
        ? moment(formInputs.ecreated_on).format("DD-MM-YYYY")
        : "",
    });
  };

  const calculateTotalCount = (chartData) => {
    if (!chartData) return 0;
    return Object.values(chartData).reduce((total, item) => {
      return total + (Number(item.totalcount) || 0);
    }, 0);
  };

  const salesFunnelOptions = {
    animationEnabled: true,
    title: { text: "" },
    data: [
      {
        type: "funnel",
        indexLabel: "{label} - {y}",
        toolTipContent: "<b>{label}</b>: {y}",
        valueRepresents: "area",
        dataPoints: Object.entries(reportData.leadsLineChartData).map(
          ([label, y]) => {
            let color;
            const labelLower = label.toLowerCase().trim();

            if (labelLower === "sales team calls") {
              color = "#ff0000";
            } else if (labelLower === "follow up calls") {
              color = "#9c27b0";
            } else if (labelLower === "trial initiation") {
              color = "#3f51b5";
            } else if (labelLower === "trial over") {
              color = "#2196f3";
            } else if (labelLower === "lead converted") {
              color = "#00bfa5";
            } else {
              color = "#cccccc";
            }

            return {
              y: parseInt(y, 10) || 0,
              label: label,
              color: color,
            };
          }
        ),
      },
    ],
  };

  const leadQualityFunnelOptions = {
    animationEnabled: true,
    title: { text: "" },
    data: [
      {
        type: "funnel",
        indexLabel: "{label} - {y}",
        toolTipContent: "<b>{label}</b>: {y}",
        valueRepresents: "area",
        dataPoints: Object.entries(reportData.leadQualityLineChartData).map(
          ([label, y]) => {
            let color;
            const labelLower = label.toLowerCase().trim();

            if (labelLower === "new") {
              color = "#f44336";
            } else if (labelLower === "follow up") {
              color = "#9c27b0";
            } else if (labelLower === "prospect") {
              color = "#3f51b5";
            } else if (labelLower === "contacted") {
              color = "#2196f3";
            } else if (labelLower === "subscribed") {
              color = "#00bfa5";
            } else {
              color = "#cccccc";
            }

            return {
              y: parseInt(y, 10) || 0,
              label: label,
              color: color,
            };
          }
        ),
      },
    ],
  };

  // Skeleton loader for summary cards
  const SummarySkeleton = () => (
    <div className="row mb-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div className="col-lg-3" key={index}>
          <div className="widget widget-stats bg-gradient-skeleton skeleton-card">
            <div className="widget-stats-left">
              <div className="skeleton-text" style={{ width: "70%", height: "16px", marginBottom: "10px" }}></div>
            </div>
            <div className="widget-stats-right">
              <div className="skeleton-text" style={{ width: "50%", height: "28px" }}></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Skeleton loader for funnel charts
  const FunnelSkeleton = () => (
    <div className="row">
      <div className="col-12 text-center my-2">
        <h4 className="skeleton-text" style={{ width: "200px", margin: "0 auto", height: "24px" }}></h4>
      </div>
      <div className="col-sm-6">
        <div style={{ height: "370px", maxWidth: "480px", margin: "0px auto" }} className="skeleton-chart-container">
          <div className="skeleton-chart" style={{ height: "100%", width: "100%" }}>
            <div className="skeleton-funnel"></div>
          </div>
        </div>
      </div>
      <div className="col-sm-6">
        <div style={{ height: "300px", position: "relative" }} className="skeleton-chart-container">
          <div className="skeleton-chart" style={{ height: "100%", width: "100%" }}>
            <div className="skeleton-line-chart"></div>
          </div>
        </div>
      </div>
    </div>
  );

  // Skeleton loader for individual chart sections
  const ChartSectionSkeleton = () => (
    <div className="row">
      <div className="col-12 text-center my-2">
        <h4 className="skeleton-text" style={{ width: "180px", margin: "0 auto", height: "24px" }}></h4>
      </div>
      <div className="col-sm-6 mb-3">
        <div className="pie_chart_wrap">
          <div className="pie_chart_div" style={{ height: "300px", position: "relative" }}>
            <div className="skeleton-chart" style={{ height: "100%", width: "100%" }}>
              <div className="skeleton-pie-chart"></div>
            </div>
          </div>
          <div className="total_value_div text-center">
            <div className="skeleton-text" style={{ width: "120px", height: "18px", margin: "10px auto" }}></div>
          </div>
        </div>
      </div>
      <div className="col-sm-6">
        <div style={{ height: "300px", position: "relative" }}>
          <div className="skeleton-chart" style={{ height: "100%", width: "100%" }}>
            <div className="skeleton-bar-chart"></div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
      {/* Injecting the User's Requested CSS Link */}
      <link
        href="https://research.streetgains.in/dev/app/assets/daterangepicker.css"
        rel="stylesheet"
      />

      {/* Breadcrumb */}
      <div className="row pb-2">
        <div className="col-md-3">
          <ul className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard" style={{ textDecoration: "none" }}>
                <FontAwesomeIcon icon={faHome} /> Home
              </a>
            </li>
            <li className="breadcrumb-item active">
              Subscriber Analysis Chart
            </li>
          </ul>
        </div>
      </div>

      {/* Main Panel */}
      <div className="panel panel-default with-rounded-corner with-shadow chartbox">
        <div className="panel-heading"></div>
        <div className="panel-body">
          {/* Filter Form */}
          <div className="row">
            <div className="col-12">
              <form name="sesform" id="sesform" onSubmit={applyFilters}>
                <div className="row align-items-center">
                  <div className="col-md-2">
                    <div className="form-group">
                      <label className="control-label">
                        Subscriber Create Date
                      </label>
                      <DateRangePicker
                        initialSettings={{
                          autoUpdateInput: false,
                          locale: {
                            format: "DD-MM-YYYY",
                            cancelLabel: "Clear",
                          },
                          ranges: ranges,
                          startDate: formInputs.screated_on
                            ? moment(formInputs.screated_on)
                            : moment(),
                          endDate: formInputs.ecreated_on
                            ? moment(formInputs.ecreated_on)
                            : moment(),
                        }}
                        onApply={handleDateCallback}
                      >
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            value={formInputs.created_on}
                            placeholder="Select Date Range"
                            readOnly
                          />
                          <span
                            className="input-group-addon"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "0 10px",
                              background: "#eee",
                              border: "1px solid #ced4da",
                              borderLeft: "none",
                            }}
                          >
                            <FontAwesomeIcon icon={faCalendarAlt} />
                          </span>
                        </div>
                      </DateRangePicker>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group">
                      <label className="control-label w-100"></label>
                      <button type="submit" className="btn btn-default">
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Summary Boxes */}
          {loadingStates.summary ? (
            <SummarySkeleton />
          ) : (
            <div className="row mb-4">
              <div className="col-lg-3">
                <div className="widget widget-stats bg-gradient-blue-dark inverse-mode">
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">Subscriber Count</div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {reportData.subscriberCount}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3">
                <div className="widget widget-stats bg-gradient-orange inverse-mode">
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">
                      Overall Conversion Rate
                    </div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {reportData.overallConversionRate}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3">
                <div className="widget widget-stats bg-gradient-red inverse-mode">
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">Total Subscribed</div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {reportData.totalSubscribed}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3">
                <div className="widget widget-stats bg-gradient-green inverse-mode">
                  <div className="widget-stats-left">
                    <div className="widget-stats-title">Total Active</div>
                  </div>
                  <div className="widget-stats-right">
                    <div className="widget-stats-value">
                      {reportData.subscriberActiveCount}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sales Funnel Analysis */}
          <div className="row">
            <div className="col-12 text-center my-2">
              <h4>Sales Funnel Analysis</h4>
            </div>
            {loadingStates.funnels ? (
              <FunnelSkeleton />
            ) : (
              <>
                <div className="col-sm-6">
                  <div
                    style={{
                      height: "370px",
                      maxWidth: "480px",
                      margin: "0px auto",
                    }}
                  >
                    <CanvasJSChart options={salesFunnelOptions} />
                  </div>
                </div>
                <div className="col-sm-6">
                  <div style={{ height: "300px", position: "relative" }}>
                    <canvas
                      ref={(el) => (chartRefs.current.statisticsChart = el)}
                      id="statisticsChart"
                    ></canvas>
                  </div>
                </div>
              </>
            )}
          </div>

          <hr className="mt-5" />

          {/* Lead Funnel Analysis */}
          <div className="row">
            <div className="col-12 text-center my-2">
              <h4>Lead Funnel Analysis</h4>
            </div>
            {loadingStates.funnels ? (
              <FunnelSkeleton />
            ) : (
              <>
                <div className="col-sm-6">
                  <div
                    style={{
                      height: "370px",
                      maxWidth: "480px",
                      margin: "0px auto",
                    }}
                  >
                    <CanvasJSChart options={leadQualityFunnelOptions} />
                  </div>
                </div>
                <div className="col-sm-6">
                  <div style={{ height: "300px", position: "relative" }}>
                    <canvas
                      ref={(el) =>
                        (chartRefs.current.leadQualityStatisticsChart = el)
                      }
                      id="leadQualityStatisticsChart"
                    ></canvas>
                  </div>
                </div>
              </>
            )}
          </div>

          <hr className="mt-5" />

          {/* Dynamic Chart Sections */}
          <div className="row">
            {chartSections.map((ckey) => (
              <React.Fragment key={ckey}>
                <div className="col-12 text-center my-2">
                  <h4>{getSectionTitle(ckey)}</h4>
                </div>

                {loadingStates[ckey] ? (
                  <ChartSectionSkeleton />
                ) : reportData.chartData[ckey] ? (
                  <>
                    <div className="col-sm-6 mb-3">
                      <div className="pie_chart_wrap">
                        <div
                          className="pie_chart_div"
                          style={{ height: "300px", position: "relative" }}
                        >
                          <canvas
                            ref={(el) =>
                              (chartRefs.current[`pieChart_${ckey}`] = el)
                            }
                            id={`pieChart_${ckey}`}
                          ></canvas>
                        </div>
                        <div className="total_value_div text-center">
                          <label>
                            Total Count:{" "}
                            <span>
                              {calculateTotalCount(reportData.chartData[ckey])}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div style={{ height: "300px", position: "relative" }}>
                        <canvas
                          ref={(el) =>
                            (chartRefs.current[`lineChart_${ckey}`] = el)
                          }
                          id={`lineChart_${ckey}`}
                        ></canvas>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="col-12 text-center">
                    <p>No data available for this section.</p>
                  </div>
                )}
                <hr className="w-100" />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        /* Existing styles remain unchanged */
        .breadcrumb {
          background: transparent;
          margin-bottom: 0;
          padding: 0;
          list-style: none;
          display: flex;
          align-items: center;
        }

        .breadcrumb-item {
          display: flex;
          align-items: center;
        }

        .breadcrumb-item + .breadcrumb-item::before {
          content: ">";
          padding: 0 8px;
          color: #6c757d;
        }

        .breadcrumb-item a {
          color: #007bff;
          text-decoration: none;
        }

        .breadcrumb-item.active {
          color: #6c757d;
        }

        .panel {
          background: white;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .with-rounded-corner {
          border-radius: 4px;
        }

        .with-shadow {
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .widget-stats {
          position: relative;
          padding: 15px;
          border-radius: 4px;
          min-height: 55px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: #fff;
          overflow: hidden;
        }

        .widget-stats:before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.1);
          z-index: 0;
        }

        .widget-stats-left,
        .widget-stats-right {
          position: relative;
          z-index: 10;
        }

        .widget-stats-title {
          font-size: 14px;
          font-weight: 600;
          opacity: 0.9;
          margin-bottom: 5px;
        }

        .widget-stats-value {
          font-size: 28px;
          font-weight: 700;
          line-height: 1;
        }

        .bg-gradient-blue-dark {
          background: linear-gradient(45deg, #2c3e50, #3498db);
        }

        .bg-gradient-orange {
          background: linear-gradient(45deg, #e67e22, #e74c3c);
        }

        .bg-gradient-red {
          background: linear-gradient(45deg, #c0392b, #e74c3c);
        }

        .bg-gradient-green {
          background: linear-gradient(45deg, #27ae60, #2ecc71);
        }

        .inverse-mode {
          color: #fff;
        }

        .pie_chart_wrap {
          position: relative;
        }

        .total_value_div {
          margin-top: 10px;
          font-weight: bold;
        }

        canvas {
          max-width: 100%;
        }

        .canvasjs-chart-credit {
          display: none !important;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .control-label {
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: block;
        }

        .btn-default {
          background-color: #f8f9fa;
          border-color: #ddd;
          color: #333;
        }

        .btn-default:hover {
          background-color: #e9ecef;
          border-color: #ccc;
        }

        .input-group {
          display: flex;
          width: 100%;
        }
        .input-group .form-control {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          cursor: pointer;
          background: #fff !important;
        }
        .input-group-addon {
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }

        /* Skeleton Loading Styles */
        .skeleton-text {
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          );
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
          border-radius: 4px;
          display: inline-block;
        }

        .skeleton-card {
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          ) !important;
          background-size: 200% 100% !important;
          animation: skeleton-loading 1.5s ease-in-out infinite !important;
          min-height: 55px;
        }

        .skeleton-chart-container {
          background: #f8f9fa;
          border-radius: 4px;
          overflow: hidden;
        }

        .skeleton-chart {
          position: relative;
          overflow: hidden;
        }

        .skeleton-funnel,
        .skeleton-pie-chart,
        .skeleton-bar-chart,
        .skeleton-line-chart {
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          );
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
        }

        .skeleton-funnel {
          clip-path: polygon(50% 0%, 100% 0, 100% 100%, 0 100%, 0 0, 50% 0%);
        }

        .skeleton-pie-chart {
          border-radius: 50%;
          width: 80% !important;
          height: 80% !important;
          margin: 10% auto;
        }

        .skeleton-bar-chart {
          display: flex;
          align-items: flex-end;
          justify-content: space-around;
          padding: 20px;
        }

        .skeleton-bar-chart::before,
        .skeleton-bar-chart::after {
          content: '';
          width: 15%;
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          );
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
          border-radius: 4px;
        }

        .skeleton-bar-chart::before {
          height: 70%;
        }

        .skeleton-bar-chart::after {
          height: 50%;
        }

        .skeleton-line-chart {
          position: relative;
        }

        .skeleton-line-chart::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 10%;
          right: 10%;
          height: 3px;
          background: linear-gradient(
            90deg,
            #f0f0f0 25%,
            #e0e0e0 50%,
            #f0f0f0 75%
          );
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s ease-in-out infinite;
          border-radius: 2px;
        }

        @keyframes skeleton-loading {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
      `}</style>
    </div>
  );
};

export default SubscriberAnalysisChart;